import { Question } from "@/types/agent"
import type {
  CustomAgentNotifyPolicy,
  CustomAgentOutputType,
} from "@/types/custom-agent"
import { LocationSearchHit, PlacePhoto } from "@/types/place"
import {
  AgentStatusEntityPreviewItem,
  AgentStatusFlightNotePreviewItem,
  AgentStatusFlightSearchPreviewItem,
  AgentStatusMetricPayload,
  AgentStatusQuotePreviewItem,
} from "@/types/socket"
import { DelayReason } from "@/types/streamed-messages"
import type { TripRef } from "@/types/trip"

import { AssistantSuggestionRef } from "./assistant-suggestion"
import { CollectionRef, CollectionSummary } from "./collection"
import { DayObject, Timestamp } from "./date"
import { AirlineRef, InspirationRef, PlaceRef } from "./entity"
import { ExploreLocation } from "./explore"
import { Airline } from "./flights/airline"
import {
  FlightSearchSummary,
  FlightTopEntry,
} from "./flights/flight-search-summary"
import { FolioDocumentRef } from "./folio/folio-document"
import { InspirationSummary } from "./inspiration"
import { Itinerary } from "./itinerary"
import { PlaceSummary } from "./place"
import type { SearchRoute } from "./route"
import {
  ThinTripSummary,
  TravelerGroup,
  Trip,
  TripChange,
  TripLogItem,
} from "./trip"
import { PublicUser, UserRef } from "./user"
import { Optional } from "./util"
import {
  WebSearchResult,
  WebSearchResultEvent,
  WebSearchResultTrail,
} from "./web-search-result"

export const CHAT_MESSAGE_MAX_LENGTH = 2048

export type Vertical = "flights" | "stays"

export interface ThinChatSummary {
  id: number
  name?: string
  is_group?: boolean
  memory_enabled?: boolean
  trip: ThinTripSummary
  last_activity_at: Timestamp
}

// Another trip member's individual chat, readable by the trip's other members
export interface CollaboratorChat {
  id: number
  name?: string
  user_ref: UserRef
  last_activity_at: Timestamp
}

export interface ChatSummary {
  id: number
  name?: string
  trip: Trip
  group: boolean
  directed_flow?: ChatDirectedFlow
  last_message_at?: Timestamp
  vertical?: Vertical | null
  anonymous_sharing?: boolean
  /** Derived from chat content; omit falls back to trip cover. */
  cover_photo?: PlacePhoto
}

export interface Chat extends ChatSummary {
  messages: Array<PersistentMessage>
  log_items: Array<TripLogItem>
  more_messages: boolean // true if there are older messages than these
  stream_ids: string[]
  geo_hint?: string
  // the trip member's chat this group chat continues
  initial_chat?: CollaboratorChat
  orgid?: string
  // Long-term memory active for this chat (account AND per-chat). Absent ⇒ treat as on.
  memory_enabled?: boolean
  // Whether the per-chat memory setting can still be toggled (only before the first message).
  memory_setting_editable?: boolean
}

export interface SharedChat {
  share_user: PublicUser
  chat_id: number
  chat_name?: string
  vertical?: Vertical | null
  messages: Array<PersistentMessage>
  more_messages: boolean
}

export type Message = PersistentMessage | InProgressMessage | RecapMessage
export type PersistentMessage = BotMessage | CustomerMessage

export type MessageRef = `m-${string}`
const MessageRefRE = /^m-[a-zA-Z0-9]{8}$/

export const CUSTOMER_MESSAGE_SUBTYPES = [
  "suggested-question",
  "chat-action-pill",
  "trip-advice-action-pill",
  "assistant-suggestion-action-pill",
  "setup-action-pill",
  "itinerary-review-fix-all",
  "external-chat-cta",
  "memory-question",
  "search-form",
  "clarifying-answer",
] as const

export type CustomerMessageSubtype = (typeof CUSTOMER_MESSAGE_SUBTYPES)[number]

interface AbstractMessage {
  body: string
  timestamp: Timestamp
  markers: MessageMarker[]
  places: PlaceSummary[]
  inspirations: InspirationSummary[]
  airlines?: Airline[]
  collections?: CollectionSummary[]
  quote_refs?: string[] // hq-* refs for hotel quotes associated with this message
  structured_results?: {
    events?: WebSearchResultEvent[]
    trails?: WebSearchResultTrail[]
    routes?: SearchRoute[]
  }
  // Set on both roles for a turn spoken through a voice call — a call is a run
  // of customer AND bot turns, so the call id is the only thing that can group
  // one. voice_call_started_at is the call's own start (it precedes the first
  // persisted turn, which cannot see the connect or the unpersisted greeting).
  voice_call_id?: string
  voice_call_started_at?: Timestamp
}

export interface CustomerMessage extends AbstractMessage {
  type: "customer"
  id: number
  edited_at?: Timestamp
  lock_version: number
  user?: PublicUser // undefined when the user is anonymous or deactivated
  attachments?: Attachment[]
  customer_purpose?: string // e.g., "recommendations:restaurant"
  voice_message?: boolean
}

export interface BotMessage extends AbstractMessage {
  type: "bot"
  sub_type?: BotMessageSubtype
  id: number
  lock_version: number
  has_flight_search_summary?: boolean
  types_of_interest?: Optional<string[]>
  suggested_questions?: { title: string; text: string }[]
  clarifying_questions?: Question[]
  action_pills?: ActionItem[]
  stream_id?: string
  intent?: Intent
  matching_inspiration_id?: number
  matching_event_ids?: number[]
  has_suggestion_for_followup?: boolean
  skip_followup?: boolean
  disable_place_carousel?: boolean
  immediate_followup?: boolean
  thinking_duration_seconds?: number
  thought_for_label?: string
  has_activity_feed?: boolean
  stopped_by_user?: boolean

  trip_change?: TripChange
  sources?: MessageSourcePage[]
}

// The page behind a link the body cites as a source ([United](https://... "source"))
export interface MessageSourcePage {
  url: string
  title: string
  date?: DayObject
}

export interface RecapMessage extends AbstractMessage {
  type: "recap"
  id: number
}

interface AbstractActionPill {
  ref?: string
  label: string
  title?: string
  description?: string
}

export interface MoreLikeTheseActionPill extends AbstractActionPill {
  type: "more_like_these"
  context: {
    query_type?: string
    location?: string
  }
}

export interface SaveActionPill extends AbstractActionPill {
  type: "add_to_trip" | "save_to_collection"
  context: {
    entity_refs: PlaceRef[]
    target_id?: number
    location_geo_id?: number
  }
}

export interface AdditionalDetailsActionPill extends AbstractActionPill {
  type: "additional_details"
  context: {
    entity_refs: PlaceRef[]
    entity_type?: string
  }
}

export interface SimpleActionPill extends AbstractActionPill {
  type:
    | "continue_chat"
    | "generate_itinerary"
    | "start_new_chat"
    | "quiz" // historical only; filtered out of UI
    | "create_itinerary"
    | "share_current_location"
}

export interface CreateTripActionPill extends AbstractActionPill {
  type: "create_trip"
  context?: {
    destination_entity_ref?: PlaceRef
  } | null
}

export interface ShowItineraryActionPill extends AbstractActionPill {
  type: "show_itinerary"
  context?: {
    itinerary_event_ids?: number[]
  }
}

export interface ShowBookingActionPill extends AbstractActionPill {
  type: "show_booking"
  context?: {
    itinerary_event_id?: number
    folio_document_ref?: FolioDocumentRef
  }
}

export interface ShowDisruptionsActionPill extends AbstractActionPill {
  type: "show_disruptions"
}

export interface OpenChatActionPill extends AbstractActionPill {
  type: "open_chat"
  context: {
    chat_id: number
  }
}

/** The server starts the chat from an opening only it holds; the client sends the pill's ref. */
export interface StartServerChatActionPill extends AbstractActionPill {
  type: "start_server_chat"
  ref: string
}

export interface ShowPlaceActionPill extends AbstractActionPill {
  type: "show_place"
  context: {
    /** A place, or a web event (`we-…`), which opens its own sheet. */
    entity_ref: PlaceRef | `we-${string}`
  }
}

export interface ViewGuidesActionPill extends AbstractActionPill {
  type: "view_guides"
  context: {
    search?: string
    prompt?: string
  }
}

export interface CreateChatActionPill extends AbstractActionPill {
  type: "create_chat"
  context: {
    prompt?: string
    kickoff_context?: string
    interaction_hint?: "quick-answer" | "conversation"
    /** The trip the chat belongs to, when the pill is about one; otherwise the chat makes its own. */
    trip_ref?: TripRef
  }
}

export interface CallPlaceActionPill extends AbstractActionPill {
  type: "call_place"
  context: {
    phone_number: string
    place_name?: string
    entity_ref?: PlaceRef
  }
}

export interface GetDirectionsActionPill extends AbstractActionPill {
  type: "get_directions"
  context: {
    destination_address: string
    origin_address?: string
    destination_name?: string
    destination_entity_ref?: PlaceRef
  }
}

/** Activates an assistant suggestion's proposed custom agent: the client posts
 *  /assistant_suggestions/:ref/create_agent (the composer gate stays server-side);
 *  the agent fields ride along so the customize dialog can prefill. */
export interface CreateCustomAgentActionPill extends AbstractActionPill {
  type: "create_custom_agent"
  context: {
    suggestion_ref: AssistantSuggestionRef
    idea_string?: string
    anchor?: string
    cadence?: string
    schedule_hint?: string
    output_type?: CustomAgentOutputType
    notify_policy?: CustomAgentNotifyPolicy
  }
}

/** Opens an external page — the airline or venue site behind a disruption advice card. */
export interface OpenUrlActionPill extends AbstractActionPill {
  type: "open_url"
  context: {
    url: string
  }
}

/** Opens Home's flight search modal, as the "Flights" pill above the feed does. */
export interface SearchFlightsActionPill extends AbstractActionPill {
  type: "search_flights"
  context?: never
}

/** Starts the travel-assistant setup flow. */
export interface SetUpAssistantActionPill extends AbstractActionPill {
  type: "set_up_assistant"
  context?: never
}

/** Makes the named place the profile's home city; the server applies it when the click is recorded. */
export interface SetHomeLocationActionPill extends AbstractActionPill {
  type: "set_home_location"
  context: {
    entity_ref: PlaceRef
    name: string
  }
}

export type ActionPill =
  | MoreLikeTheseActionPill
  | SaveActionPill
  | AdditionalDetailsActionPill
  | SimpleActionPill
  | CreateTripActionPill
  | ShowItineraryActionPill
  | ShowBookingActionPill
  | ShowDisruptionsActionPill
  | OpenChatActionPill
  | StartServerChatActionPill
  | ShowPlaceActionPill
  | ViewGuidesActionPill
  | CreateChatActionPill
  | CreateCustomAgentActionPill
  | CallPlaceActionPill
  | GetDirectionsActionPill
  | OpenUrlActionPill
  | SearchFlightsActionPill
  | SetUpAssistantActionPill
  | SetHomeLocationActionPill

interface AbstractActionCard {
  label: string
  title: string
  description: string
}

export interface CreateTripActionCard extends AbstractActionCard {
  type: "create_trip_card"
}

export interface SwitchToMindtripActionCard extends AbstractActionCard {
  type: "switch_to_mindtrip_card"
  context: {
    prompt: string
  }
}

export type ActionCard = CreateTripActionCard | SwitchToMindtripActionCard

export const ACTION_CARD_TYPES = Object.freeze([
  "create_trip_card",
  "switch_to_mindtrip_card",
])

export type ActionItem = ActionPill | ActionCard

export type ActivityFeedEntry =
  /** icon_domains: sites the line is about, shown as favicons before it. */
  | { type: "status"; text: string; icon_domains?: string[] }
  /** Pages a website read visited, shown as one ticker line. */
  | { type: "site_visits"; urls: string[] }
  | {
      type: "entity_preview"
      items: AgentStatusEntityPreviewItem[]
      title?: string
      area_count?: number
      unavailable_pct?: number
    }
  | {
      type: "quote_preview"
      items: AgentStatusQuotePreviewItem[]
      title?: string
    }
  | {
      type: "flight_preview"
      items: AgentStatusFlightSearchPreviewItem[]
      title?: string
    }
  | {
      type: "flight_note_preview"
      items: AgentStatusFlightNotePreviewItem[]
      title?: string
      total_count?: number
      max_display?: number
    }
  /** One row of running counts for the loyalty work, replaced in place as it climbs. */
  | {
      type: "loyalty_progress"
      hotels_weighed: number
      cash_wins: number
      points_wins: number
      quotes_with_points: number
      icon_domains?: string[]
    }

export interface InProgressMessage extends AbstractMessage {
  type: "bot"
  sub_type?: BotMessageSubtype
  id: "in-progress"
  markers: MessageMarker[]
  has_flight_search_summary?: boolean
  flight_search_summary?: FlightSearchSummary
  isGeneratingItinerary?: boolean
  itinerary?: Itinerary
  delayReason?: Optional<DelayReason>
  delayQuip?: string
  delayText?: string
  delayPlace?: PlaceSummary
  delayPercent?: number
  delayComplication?: string
  delayInterruptible?: boolean
  delayInterruptibleTool?: string
  delayInterruptibleSessionId?: number
  agentSessionId?: number
  metricPayload?: AgentStatusMetricPayload
  activityFeed?: ActivityFeedEntry[]
  thinkingDurationSeconds?: number
  intent?: Intent
  chat?: ChatSummary
  trip?: Trip
  searchGeo?: LocationSearchHit
  stopped_by_user?: boolean
}

export type BotMessageSubtype =
  | "follow-up"
  | "initial-greeting"
  | "clarifying-question"
  | "directed-flow-kickoff"

// Marks a bit of text in a message body where an entity has been detected.
export type MessageMarker =
  | PlaceMarker
  | InspirationMarker
  | UserMarker
  | EventMarker
  | AirlineMarker
  | FlightMarker
  | CardMarker
  | CollectionMarker
interface AbstractMarker {
  start: number
  end: number
}
export interface PlaceMarker extends AbstractMarker {
  type: "place"
  ref?: PlaceRef // undefined when this entity isn't known to us but the bot flagged it
  hide_pin?: boolean
  at?: number // for matching schedule start_at timestamp for event places
}
export interface InspirationMarker extends AbstractMarker {
  type: "inspiration"
  ref?: InspirationRef // undefined when this entity is no longer available
}
export interface UserMarker extends AbstractMarker {
  type: "user"
  special_type?: "all" | "mindtrip"
  ref?: UserRef // undefined when this is a special mention like 'all'
}
export interface EventMarker extends AbstractMarker {
  type: "event"
  structured_result?: string // event ID reference from web_search_structured.events
}
export interface AirlineMarker extends AbstractMarker {
  type: "airline"
  ref?: AirlineRef
}
export interface CollectionMarker extends AbstractMarker {
  type: "collection"
  ref?: CollectionRef // undefined when the collection is gone or the viewer can't open it
}
export interface FlightMarker extends AbstractMarker {
  type: "flight"
  flight_id: string // e.g. "FlightOption:39" | "FlightId:12"
  flight_details?: FlightTopEntry
}
export interface CardMarker extends AbstractMarker {
  type: "card"
  signal_id: string
  isStreaming?: boolean
  ref?: PlaceRef
  entity_type?: string
  hide_pin?: boolean // if true, don't show this place as a pin on the map
  at?: number // for events with multiple schedules, matches the schedule start_at timestamp
  quote_ref?: string // hq-* reference when this card represents a bookable hotel quote
  /** "events:N" when the card's entity is a web event rather than a place */
  structured_result?: string
  collection_ref?: CollectionRef // when the card's entity is one of the viewer's collections
}

export type ChatItem = MessageChatItem | TripLogItemChatItem | VoiceCallChatItem

export interface MessageChatItem {
  type: "message"
  timestamp: Timestamp
  message: PersistentMessage | RecapMessage
}

// Closes out a finished voice call's run of turns: the record that reports how
// long the call lasted and collects a rating for it. Anchored to the run's last
// bot turn, which is what a rating is filed against.
export interface VoiceCallChatItem {
  type: "voice-call"
  timestamp: Timestamp
  callId: string
  // Null when the call's turns cannot measure it — a run of one turn whose call
  // carried no start stamp. The record then reports the call without a length
  // rather than claiming it took no time.
  durationSeconds: number | null
  anchorMessageId: number
}

export interface TripLogItemChatItem {
  type: "trip-log-item"
  timestamp: Timestamp
  items: TripLogItem[]
}

/*
  H1) Hotels
  R1) Restaurants
  F1) Flight information, schedules, routes, availability, and pricing
  D1) Destinations
  A1) Activities
  X1) The question has nothing to do with travel
  G1) Some other question related to travel
*/
export type IntentCodes = "H1" | "R1" | "F1" | "D1" | "A1" | "X1" | "G1"

export interface Intent {
  travelers?: TravelerGroup
  start_date?: DayObject
  end_date?: DayObject

  code?: IntentCodes
  street_address?: string
  latitude?: number
  longitude?: number
}

export type FlightTripTypes = "round-trip" | "one-way" | "multi-city"
export const FlightTripTypeValues: ReadonlyArray<FlightTripTypes> = [
  "one-way",
  "round-trip",
  "multi-city",
]
export const FlightTripTypeMap: Readonly<Record<FlightTripTypes, string>> = {
  "one-way": "One way",
  "round-trip": "Round trip",
  "multi-city": "Multi-city",
}

export type FlightCabinTypes =
  | "basic"
  | "economy"
  | "premium"
  | "business"
  | "first"
export const FlightCabinTypeMap: Readonly<Record<FlightCabinTypes, string>> = {
  basic: "Basic Economy",
  economy: "Economy",
  premium: "Premium Economy",
  business: "Business",
  first: "First",
}

export interface FlightIntent {
  origin: {
    iata: string
    iatas: string[]
  }
  dest: {
    iata: string
    iatas: string[]
  }
  trip_type?: FlightTripTypes
  airlines?: string[]
  cabin?: FlightCabinTypes
  travelers?: number
  additional_transportation?: boolean
}

export type ParsedBodyPiece =
  | TextPiece
  | PlacePiece
  | InspirationPiece
  | UserPiece
  | EventPiece
  | AirlinePiece
  | FlightPiece
  | CardPiece
  | CollectionPiece

interface AbstractPiece {
  id: number
  text: string
}
interface TextPiece extends AbstractPiece {
  type: "text"
}
interface PlacePiece extends AbstractPiece {
  type: "place"
  place?: PlaceSummary // undefined if place is unknown
  at?: number // for matching schedule start_at timestamp for event places
  isStreaming?: boolean
}
interface InspirationPiece extends AbstractPiece {
  type: "inspiration"
  inspiration?: InspirationSummary // undefined if inspiration is unpublished
}
interface UserPiece extends AbstractPiece {
  type: "user"
  special_type?: "all" | "mindtrip"
  user_ref?: UserRef // undefined if user is no longer known or special type like 'all'
}
interface EventPiece extends AbstractPiece {
  type: "event"
  structured_result: string
  // resolved_data is only present if the message has been persisted and the event was resolved
  resolved_data?: WebSearchResult
}
interface AirlinePiece extends AbstractPiece {
  type: "airline"
  airline?: Airline // undefined if airline is unknown
}
interface CollectionPiece extends AbstractPiece {
  type: "collection"
  collection?: CollectionSummary // undefined when the collection is gone or not viewable
}
interface FlightPiece extends AbstractPiece {
  type: "flight"
  flight_id: string
  flight_details?: FlightTopEntry
}
export interface CardPiece extends AbstractPiece {
  type: "card"
  place?: PlaceSummary
  collection?: CollectionSummary
  /** The web event a card draws when its entity isn't a place */
  resolved_data?: WebSearchResult
  entity_type?: string
  marker: CardMarker
}
export type AttachmentType = "link" | "file"
export type AttachmentDataType = "video" | "image" | "pdf" | "article"

export type Attachment = {
  type: AttachmentType
  url?: string
  metadata?: AttachmentMetadata
  // data_type is unknown until it hits the server
  data_type?: AttachmentDataType
}

export interface ClientAttachment extends Attachment {
  id: string
  loading?: boolean
}

export interface AttachmentMetadata {
  title?: string
  description?: string
  thumbnail_url?: string
  handle?: string
  status_code?: number
  error?: string
  orgid?: string
}

export interface ChatDirectedFlow {
  key: "update-memories"
  status: "active" | "completed"
  anchor_message_id?: number
  completed_at?: string
}

export interface ChatRecap {
  body: string
  timestamp: Timestamp
}

export type InitChatProps = {
  initChat: Chat
  userLocation?: ExploreLocation
  openTripSheet?: boolean
}

export type ChatMessageLoadingStatus =
  | "idle"
  | "loading-old"
  | "loading-new"
  | "error"

export function isMessageRef(value: string): value is MessageRef {
  return MessageRefRE.test(value)
}
